import { EventCanGoLive, Pool, TicketBody, TicketDetailsParams } from './types';
import { addTicketTypeToPool, createTicket, deleteTicket, getEventPools, updateTicket } from './services';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { NEW_TICKET } from './utils';
import { ROUTES } from 'features/routes';
import { AxiosResponse } from 'axios';
import { camelizeKeys } from 'humps';
import { getData } from 'services/api';
import { queryClient } from 'index';
import { camelize, sendGAEvent } from 'utils/utils';
import { uploadImage } from 'services/UploadImage/uploadImage';
import { useToastNotification } from 'hooks/useToastNotification';
import { useTranslation } from 'react-i18next';

// Minimal shape of a row returned by the /ticket_types create POST — only the id is read (to attach a pool).
// The Go backend returns the all-caps `ID`; some paths return lowercase `id`, so both are accepted.
interface TicketCreateRow {
	ID?: number;
	id?: number;
}

export const useTicketTypeApi = () => {
	const { push } = useHistory();
	const { t } = useTranslation();
	const { id: eventId, ticketId } = useParams<TicketDetailsParams>();
	const { pathname } = useLocation();
	const { showNotification, TYPES } = useToastNotification();

	const isNewTicket = pathname.includes(NEW_TICKET);

	const { data: ticket, isError: isTicketError } = useQuery({
		queryKey: ['ticket', ticketId],
		queryFn: (): Promise<TicketBody> => getData(`/ticket_types/${ticketId}`),
		/* The accordion form mounts this hook on the show dashboard, whose URL names no ticket. */
		enabled: !isNewTicket && !!ticketId,
	});

	const { data: canGoLive } = useQuery({
		queryKey: ['canGoLive', eventId],
		queryFn: (): Promise<EventCanGoLive> => {
			const searchParams = new URLSearchParams([['set_live', 'false']]);
			return getData(`/events/${eventId}/go-live?` + searchParams.toString());
		},
		select: (data) => camelizeKeys(data) as EventCanGoLive,
		enabled: !!eventId,
	});

	const { data: pools, isError: isPoolsError } = useQuery({
		queryKey: ['eventPools', eventId],
		queryFn: (): Promise<Pool[]> => getEventPools(Number(eventId)),
		enabled: !!eventId,
		select: (data) => camelize(data) as Pool[],
	});

	const { mutateAsync: mutateUploadImage } = useMutation(uploadImage, {
		onError: () => {
			showNotification({
				message: t('error-uploading-image'),
				type: TYPES.ERROR,
			});
		},
	});

	const { mutate: mutateCreateTicket } = useMutation(createTicket, {
		onError: () => {
			showNotification({ message: t('ticketType-save-failed', 'Failed to save ticket type'), type: TYPES.ERROR });
		},
		// `response` is the /ticket_types POST result (the batch endpoint returns the created row(s));
		// `variables` is the posted, decamelized TicketBody. `tickets_pool_id` is the only field read here.
		onSuccess: async (response: AxiosResponse<TicketCreateRow | TicketCreateRow[]>, variables: TicketBody) => {
			// The batch ticket-create POST does not persist the pool FK — attach membership via the pool
			// endpoint (the path that works) when a pool was chosen.
			const created = Array.isArray(response.data) ? response.data[0] : response.data;
			const newId: number | undefined = created?.ID ?? created?.id;
			const chosenPoolId = Number(variables.tickets_pool_id);
			if (newId && chosenPoolId > 0) {
				try {
					await addTicketTypeToPool(Number(eventId), chosenPoolId, newId);
				} catch {
					// Non-fatal; the ticket saved and can be attached from the Pools form.
				}
			}
			// Membership may have changed via the pool attach above — refresh the ticket-type list, the event,
			// and the pool tile/analytics (mirrors the accordion TicketTypeForm create).
			await queryClient.invalidateQueries({ queryKey: ['ticketTypes', eventId] });
			await queryClient.invalidateQueries({ queryKey: ['event', eventId] });
			await queryClient.invalidateQueries({ queryKey: ['eventPools', eventId] });
			sendGAEvent({ category: 'Journeys', action: 'Create ticket' });
			push(ROUTES.DASHBOARD_PATH.replace(':id', eventId));
		},
	});

	const { mutate: mutateUpdateTicket } = useMutation(updateTicket, {
		onError: () => {
			showNotification({ message: t('ticketType-save-failed', 'Failed to save ticket type'), type: TYPES.ERROR });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['ticket', ticketId] });
			queryClient.invalidateQueries({ queryKey: ['event', eventId] });

			sendGAEvent({ category: 'Journeys', action: 'Edit ticket' });
			push(ROUTES.DASHBOARD_PATH.replace(':id', eventId));
		},
	});

	const { mutateAsync: mutateDeleteTicket } = useMutation(deleteTicket, {
		onSuccess: () => {
			push(ROUTES.DASHBOARD_PATH.replace(':id', eventId));
		},
		onError: () => {
			showNotification({
				message: t('ticketDetails-delete-error'),
				type: TYPES.ERROR,
			});
		},
	});

	return {
		ticket,
		isTicketError,
		canGoLive,
		pools,
		isPoolsError,
		mutateUploadImage,
		mutateCreateTicket,
		mutateUpdateTicket,
		mutateDeleteTicket,
	};
};
